import type { KeyboardEvent, ReactNode } from 'react'
import { ArrowRight, Loader2, Lock } from 'lucide-react'
import { Column } from 'src/shared/components/Semantic/all'
import { cn } from 'src/shared/components/ui/utils'

/** Primary checkout CTA — title, description, and arrow. */
export const CheckoutActionCard = ({
  title,
  description,
  onClick,
  isLoading,
  disabled,
  locked,
}: {
  title: string
  description: ReactNode
  onClick: () => void
  isLoading?: boolean
  disabled?: boolean
  locked?: boolean
}) => {
  const isDisabled = disabled || isLoading

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (isDisabled) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onClick()
    }
  }

  return (
    <div
      role="button"
      tabIndex={isDisabled ? -1 : 0}
      onClick={isDisabled ? undefined : onClick}
      onKeyDown={handleKeyDown}
      aria-disabled={isDisabled || undefined}
      className={cn(
        'group relative flex w-full items-center gap-5 rounded-[20px] px-8 py-6 text-left',
        'border border-[hsl(var(--border-inner))] bg-[linear-gradient(165deg,hsl(var(--surface-panel))_0%,hsl(var(--muted))_100%)]',
        'shadow-[0_1px_0_rgba(255,255,255,0.8)_inset]',
        'transition-[background-color,border-color,opacity,transform,box-shadow]',
        !isDisabled &&
          'heartbeat-border cursor-pointer hover:border-[hsl(var(--border-inner)/0.9)] hover:bg-[linear-gradient(165deg,hsl(var(--surface-panel)/0.7)_0%,hsl(var(--muted)/0.85)_100%)] active:translate-y-px',
        isDisabled && 'pointer-events-none opacity-45',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3b4f73]/35 focus-visible:ring-offset-2'
      )}
    >
      <Column className="min-w-0 flex-1 gap-0.5">
        <span className="text-[17px] font-semibold leading-snug tracking-[-0.01em] text-[#1A1C1E]">
          {title}
        </span>
        <div className="text-[13px] leading-[1.45] text-[#6B7280]">
          {description}
        </div>
      </Column>

      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[#E5E7EB] bg-black/80 text-white shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
        {isLoading ? (
          <Loader2 className="h-[18px] w-[18px] animate-spin" />
        ) : locked ? (
          <Lock className="h-[18px] w-[18px]" strokeWidth={2} />
        ) : (
          <ArrowRight
            className="h-[18px] w-[18px] transition-transform group-hover:translate-x-0.5"
            strokeWidth={2}
          />
        )}
      </div>
    </div>
  )
}
